/**
 * Contratos de conceptos de cobro, generacion masiva de cargos, calendario de
 * mensualidades y exoneracion de mora.
 *
 * Un concepto es una PLANTILLA configurable; un cargo es una DEUDA historica e
 * inmutable en sus importes base. Los DTO reflejan esa distincion: nunca se envia
 * un concepto donde se espera un cargo.
 */

import { z } from "zod";
import { idSchema, instantSchema, operationalDateSchema } from "./common.js";

// ---------------------------------------------------------------------------
// Conceptos de cobro
// ---------------------------------------------------------------------------

/** Importe en unidad minima (centavos). Entero positivo: nunca 0 ni negativo. */
export const amountMinorSchema = z
  .number()
  .int("El importe debe ser un número entero de centavos")
  .positive("El importe debe ser mayor que cero")
  .max(1_000_000_000, "El importe excede el máximo permitido");

export const conceptAdminSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** `true` si el concepto se cobra por mes (Mensualidad). */
  handlesMonths: z.boolean(),
  amountMinor: z.number().int(),
  currency: z.string(),
  /** Tasa de mora por mil que se congelará en los cargos (15 = 1.5 % diario). */
  lateFeeRatePerMille: z.number().int(),
  active: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
  createdByName: z.string().nullable(),
  updatedByName: z.string().nullable(),
  version: z.number().int(),
  /** Cargos vigentes que dependen de este concepto. Decide si se puede eliminar. */
  activeChargeCount: z.number().int(),
  /** Grados a los que aplica. Vacío significa todos. */
  gradeIds: z.array(idSchema).default([]),
});
export type ConceptAdminDto = z.infer<typeof conceptAdminSchema>;

export const createConceptRequestSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, "El nombre debe tener al menos 3 caracteres")
    .max(80),
  description: z.string().trim().max(300).nullable().optional(),
  handlesMonths: z.boolean(),
  amountMinor: amountMinorSchema,
  /** Solo tiene sentido en conceptos con meses; se ignora en el resto. */
  lateFeeRatePerMille: z.number().int().min(0).max(1000).optional(),
  /**
   * Grados a los que aplica. Vacío u omitido significa TODOS los grados.
   *
   * Existe porque un colegio puede cobrar distinta colegiatura según el nivel, y
   * eso se modela como un concepto por banda de precio. Sin esta lista, la
   * generación automática que corre al inscribir a un alumno le crea un cargo por
   * CADA concepto mensual: con tres bandas, tres colegiaturas al mes.
   */
  gradeIds: z.array(idSchema).max(50).optional(),
});
export type CreateConceptRequest = z.infer<typeof createConceptRequestSchema>;

export const updateConceptRequestSchema = z.object({
  name: z.string().trim().min(3).max(80).optional(),
  description: z.string().trim().max(300).nullable().optional(),
  amountMinor: amountMinorSchema.optional(),
  lateFeeRatePerMille: z.number().int().min(0).max(1000).optional(),
  /**
   * Grados a los que aplica. Omitido = no se toca; lista vacía = todos los grados.
   *
   * Se distingue «omitido» de «vacío» a propósito: editar solo el monto no debe
   * borrar en silencio el alcance por grados que alguien configuró antes.
   */
  gradeIds: z.array(idSchema).max(50).optional(),
  /** Control de concurrencia optimista: obligatorio para editar. */
  expectedVersion: z.number().int().positive(),
});
export type UpdateConceptRequest = z.infer<typeof updateConceptRequestSchema>;

export const conceptListQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  estado: z.enum(["ACTIVOS", "INACTIVOS", "TODOS"]).default("ACTIVOS"),
});
export type ConceptListQuery = z.infer<typeof conceptListQuerySchema>;

// ---------------------------------------------------------------------------
// Generacion masiva de cargos
// ---------------------------------------------------------------------------

export const monthNumberSchema = z.number().int().min(1).max(12);

export const generationPreviewRequestSchema = z
  .object({
    conceptId: idSchema,
    cycleId: idSchema,
    /** Obligatorio si el concepto maneja meses. */
    months: z.array(monthNumberSchema).min(1).max(12).optional(),
    /** Obligatorio si el concepto NO maneja meses. */
    dueDate: operationalDateSchema.optional(),
    /**
     * A quién alcanza la generación. Ausente o vacío significa TODO el colegio,
     * que es como funcionaba antes y sigue siendo lo correcto para la colegiatura.
     *
     * Los tres filtros se combinan con «o»: un estudiante entra si está en alguno
     * de los grados, en alguna de las secciones o en la lista de nombres. Así se
     * puede decir «Tercero Primaria entero MÁS estos dos de Cuarto» sin tener que
     * generar dos veces.
     */
    scope: z
      .object({
        gradeIds: z.array(idSchema).max(50).optional(),
        sectionIds: z.array(idSchema).max(100).optional(),
        studentIds: z.array(idSchema).max(500).optional(),
      })
      .optional(),
  })
  .refine(
    (value) => value.months !== undefined || value.dueDate !== undefined,
    {
      message: "Debe indicar los meses o una fecha de vencimiento",
    },
  );
export type GenerationPreviewRequest = z.infer<
  typeof generationPreviewRequestSchema
>;

export const previewStudentSchema = z.object({
  studentId: idSchema,
  code: z.string(),
  fullName: z.string(),
  gradeName: z.string().nullable(),
  sectionName: z.string().nullable(),
  /** Meses que SÍ se le crearán. */
  monthsToCreate: z.array(monthNumberSchema),
  /** Meses que se omiten porque ya tiene un cargo activo equivalente. */
  monthsSkipped: z.array(monthNumberSchema),
});
export type PreviewStudentDto = z.infer<typeof previewStudentSchema>;

export const generationPreviewSchema = z.object({
  conceptId: idSchema,
  conceptName: z.string(),
  handlesMonths: z.boolean(),
  amountMinor: z.number().int(),
  currency: z.string(),
  cycleId: idSchema,
  cycleName: z.string(),
  cycleYear: z.number().int(),
  months: z.array(monthNumberSchema),
  dueDate: operationalDateSchema.nullable(),
  studentsIncluded: z.number().int(),
  chargesToCreate: z.number().int(),
  chargesSkipped: z.number().int(),
  totalAmountMinor: z.number().int(),
  students: z.array(previewStudentSchema),
  /**
   * Huella de la poblacion, el concepto y su monto en el momento de la vista previa.
   * La confirmacion la envia de vuelta; si algo cambió, el servidor la rechaza y
   * exige recalcular. Evita confirmar un lote distinto del que se revisó.
   */
  previewToken: z.string(),
  warning: z.string(),
});
export type GenerationPreviewDto = z.infer<typeof generationPreviewSchema>;

export const confirmGenerationRequestSchema =
  generationPreviewRequestSchema.and(
    z.object({
      previewToken: z.string().min(8),
      /** Identificador del lote generado en el cliente: hace idempotente el reintento. */
      batchId: idSchema,
    }),
  );
export type ConfirmGenerationRequest = z.infer<
  typeof confirmGenerationRequestSchema
>;

export const generationResultSchema = z.object({
  batchId: idSchema,
  chargesCreated: z.number().int(),
  chargesSkipped: z.number().int(),
  studentsIncluded: z.number().int(),
  totalAmountMinor: z.number().int(),
  currency: z.string(),
  /** `true` si la confirmación ya se había aplicado antes (reintento idempotente). */
  alreadyApplied: z.boolean(),
});
export type GenerationResultDto = z.infer<typeof generationResultSchema>;

// ---------------------------------------------------------------------------
// Desactivacion de concepto
// ---------------------------------------------------------------------------

export const deactivationPreviewSchema = z.object({
  conceptId: idSchema,
  conceptName: z.string(),
  /** Cargos pendientes o parciales que se cancelarán. */
  chargesToCancel: z.number().int(),
  /** Cargos con abono parcial: se conserva lo pagado y se cancela el resto. */
  chargesPartiallyPaid: z.number().int(),
  /** Cargos ya pagados: permanecen intactos. */
  chargesPaid: z.number().int(),
  capitalToCancelMinor: z.number().int(),
  lateFeeToCancelMinor: z.number().int(),
  currency: z.string(),
  studentsAffected: z.number().int(),
  previewToken: z.string(),
  warning: z.string(),
});
export type DeactivationPreviewDto = z.infer<typeof deactivationPreviewSchema>;

export const deactivateConceptRequestSchema = z.object({
  previewToken: z.string().min(8),
  operationId: idSchema,
  reason: z
    .string()
    .trim()
    .min(5, "Indique el motivo de la desactivación")
    .max(300),
  expectedVersion: z.number().int().positive(),
});
export type DeactivateConceptRequest = z.infer<
  typeof deactivateConceptRequestSchema
>;

// ---------------------------------------------------------------------------
// Calendario de mensualidades
// ---------------------------------------------------------------------------

export const monthStatusSchema = z.enum([
  "SIN_CARGO",
  "FUTURO",
  "PENDIENTE",
  "PARCIAL",
  "PAGADO",
  "VENCIDO",
  "CANCELADO",
]);

export const calendarMonthSchema = z.object({
  month: monthNumberSchema,
  monthLabel: z.string(),
  chargeId: idSchema.nullable(),
  status: monthStatusSchema,
  statusLabel: z.string(),
  statusSymbol: z.string(),
  originalAmountMinor: z.number().int(),
  capitalOutstandingMinor: z.number().int(),
  lateFeeMinor: z.number().int(),
  paidMinor: z.number().int(),
  totalOutstandingMinor: z.number().int(),
  lateFeeDays: z.number().int(),
  lateFeeWaived: z.boolean(),
  /**
   * Los tres campos siguientes completan el estado de mora del mes.
   *
   * Existen porque el dispositivo tiene que poder RECONSTRUIR el mismo estado que
   * el servidor y validar el cobro con la misma función del dominio. Sin ellos, la
   * pantalla veía solo el capital: marcar un mes con Q996 de mora y darle a cobrar
   * fallaba con «supera su saldo pendiente», porque el cliente ignoraba la mora
   * que el propio calendario acababa de mostrar.
   */
  lateFeeRatePerMille: z.number().int(),
  lateFeePaidMinor: z.number().int(),
  capitalSettledOn: operationalDateSchema.nullable(),
  startDate: operationalDateSchema,
  dueDate: operationalDateSchema,
  lateFeeStartDate: operationalDateSchema,
  selectableForPayment: z.boolean(),
  selectableForWaiver: z.boolean(),
});
export type CalendarMonthDto = z.infer<typeof calendarMonthSchema>;

export const otherChargeSchema = z.object({
  chargeId: idSchema,
  conceptId: idSchema,
  conceptName: z.string(),
  originalAmountMinor: z.number().int(),
  outstandingMinor: z.number().int(),
  paidMinor: z.number().int(),
  dueDate: operationalDateSchema.nullable(),
  status: monthStatusSchema,
  statusLabel: z.string(),
  selectableForPayment: z.boolean(),
});
export type OtherChargeDto = z.infer<typeof otherChargeSchema>;

export const studentCalendarSchema = z.object({
  studentId: idSchema,
  studentCode: z.string(),
  studentName: z.string(),
  cycleId: idSchema,
  cycleName: z.string(),
  cycleYear: z.number().int(),
  /** `null` cuando el estudiante aún no tiene asignación académica en ese ciclo. */
  gradeName: z.string().nullable(),
  sectionName: z.string().nullable(),
  /** Leyenda a mostrar cuando no hay asignación. */
  assignmentPending: z.boolean(),
  currency: z.string(),
  asOf: operationalDateSchema,
  months: z.array(calendarMonthSchema).length(12),
  otherCharges: z.array(otherChargeSchema),
  totalOutstandingMinor: z.number().int(),
  totalLateFeeMinor: z.number().int(),
});
export type StudentCalendarDto = z.infer<typeof studentCalendarSchema>;

// ---------------------------------------------------------------------------
// Exoneracion de mora
// ---------------------------------------------------------------------------

export const waiverPreviewRequestSchema = z.object({
  studentId: idSchema,
  chargeIds: z
    .array(idSchema)
    .min(1, "Seleccione al menos una mensualidad con mora")
    .max(24),
  /**
   * Mora que se ACUERDA seguir cobrando, por mensualidad, en unidades mínimas.
   *
   * Ausente o cero es la exoneración total de siempre. Existe porque en la
   * ventanilla se negocia: «son Q150 de mora, pero quedamos en que paga Q100».
   * Antes eso obligaba a exonerar los Q150 y cobrar los Q100 como si fueran otra
   * cosa, con lo que el reporte de mora exonerada decía una cifra falsa.
   */
  agreed: z.record(idSchema, z.number().int().nonnegative()).optional(),
});
export type WaiverPreviewRequest = z.infer<typeof waiverPreviewRequestSchema>;

export const waiverLineSchema = z.object({
  chargeId: idSchema,
  cycleId: idSchema,
  cycleYear: z.number().int(),
  month: monthNumberSchema,
  monthLabel: z.string(),
  lateFeeDays: z.number().int(),
  waivedMinor: z.number().int(),
  /** Mora que quedará por cobrar tras el acuerdo. Cero si se exonera entera. */
  agreedMinor: z.number().int(),
});
export type WaiverLineDto = z.infer<typeof waiverLineSchema>;

export const waiverPreviewSchema = z.object({
  studentId: idSchema,
  studentCode: z.string(),
  studentName: z.string(),
  currency: z.string(),
  asOf: operationalDateSchema,
  lines: z.array(waiverLineSchema),
  totalWaivedMinor: z.number().int(),
  previewToken: z.string(),
  /** Se recalca que NO se toca el capital. */
  notice: z.string(),
});
export type WaiverPreviewDto = z.infer<typeof waiverPreviewSchema>;

export const confirmWaiverRequestSchema = waiverPreviewRequestSchema.and(
  z.object({
    previewToken: z.string().min(8),
    operationId: idSchema,
  }),
);
export type ConfirmWaiverRequest = z.infer<typeof confirmWaiverRequestSchema>;

export const waiverResultSchema = z.object({
  operationId: idSchema,
  monthsWaived: z.number().int(),
  totalWaivedMinor: z.number().int(),
  currency: z.string(),
  alreadyApplied: z.boolean(),
});
export type WaiverResultDto = z.infer<typeof waiverResultSchema>;
